// plates.js — client twin of api/shared/plateMatch.normPlate()
// (Suresh 2026-10-06). Keep the two in sync: upper -> drop '#unit'
// suffix -> strip non-alphanumerics.
import { useCallback, useEffect, useState } from 'react';

export function normPlate(s) {
  return String(s || '').toUpperCase().replace(/#.*$/, '').replace(/[^A-Z0-9]/g, '');
}

// Camera-seen lookup for "📷 verified" marks on inbound/outbound
// lists. Fetches /api/verkada/seen (synced history) once per range;
// returns isSeen(date, plate). Fails silent — no camera data just
// means no marks.
export function useCamSeen(fromIso, toIso) {
  const [seen, setSeen] = useState(null);   // date -> Set(normPlates)
  const [probable, setProbable] = useState(null);   // date -> Set(normPlates) matched by other evidence
  const [probableIds, setProbableIds] = useState(null);   // Set(loadId) — covers plate-less (unit #) loads
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (fromIso) qs.set('from', fromIso);
        if (toIso) qs.set('to', toIso);
        const r = await fetch('/api/verkada/seen?' + qs.toString(), { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        if (dead || !j.ok) return;
        const m = {};
        for (const [d, plates] of Object.entries(j.seen || {})) m[d] = new Set(plates);
        setSeen(m);
        const pm = {};
        for (const [d, plates] of Object.entries(j.probable || {})) pm[d] = new Set(plates);
        setProbable(pm);
        const ids = new Set();
        for (const arr of Object.values(j.probableLoads || {})) for (const id of arr) ids.add(String(id));
        setProbableIds(ids);
      } catch { /* no marks */ }
    })();
    return () => { dead = true; };
  }, [fromIso, toIso]);
  const isSeen = useCallback((date, plate) => {
    if (!seen) return false;
    const s = seen[String(date || '').slice(0, 10)];
    return !!(s && s.has(normPlate(plate)));
  }, [seen]);
  // Matched to a camera visit by other evidence (trailer, misread,
  // name on truck, operator) — not a direct plate read.
  const isProbable = useCallback((date, plate, loadId) => {
    if (loadId != null && probableIds && probableIds.has(String(loadId))) return true;
    if (!probable) return false;
    const s = probable[String(date || '').slice(0, 10)];
    return !!(s && s.has(normPlate(plate)));
  }, [probable, probableIds]);
  return { isSeen, isProbable, loaded: !!seen };
}

// The standard mark — title explains the check.
export const CAM_SEEN_TITLE = 'Verified: this plate was read by the gate camera on this day';
export const CAM_PROBABLE_TITLE = 'Matched to a camera visit by other evidence (trailer, misread, name on truck, or operator) — see Yard visits';
